<template>
  <div
    :class="[
      $style.fortuneWheelBlock,
      { [$style.fortuneWheelBlockWinState]: wheelState === 'win' },
    ]"
  >
    <img
      fetchpriority="high"
      :class="$style.fortuneWheelBlockBg"
      :src="strapiFileParser(data.backgroundImage).url"
      alt=""
    />
    <div :class="$style.fortuneWheelHolder">
      <h1>{{ storeWheel.title }}</h1>
      <div :class="$style.wheelBlock">
        <img
          fetchpriority="high"
          :class="[$style.wheelBlockImg, $style.wheelBlockImgInitial]"
          :src="strapiFileParser(data.wheelImageDefault).url"
          alt=""
        />
        <img
          fetchpriority="high"
          :class="[$style.wheelBlockImg, $style.wheelBlockImgWin]"
          :src="strapiFileParser(data.wheelImageWin).url"
          alt=""
        />
        <p v-if="wheelState !== 'win' && numberOfSpins > 0" :class="$style.wheelBlockSpinsLeft">
          {{ data.spinsLeftText }} {{ numberOfSpins }}
        </p>
        <div :class="$style.wheelDoors">
          <div v-for="i in 3" :key="i" ref="doorsRefs" :class="$style.wheelDoor">
            <div ref="boxesRefs" class="boxes" :class="$style.wheelBoxes" />
          </div>
        </div>
        <button
          :class="[
            $style.wheelBtnSpin,
            {
              [$style.loading]:
                isLoading || (!storeSsLootboxes.isLoadedOnce && storeSsPlayer.isPlayerAuthorised),
            },
          ]"
          :disabled="wheelState !== null"
          @click="handleSpinClick"
        >
          <span>{{ buttonTitle }}</span>
          <img src="/images/wheel/ico_loading.svg" alt="" />
        </button>
      </div>
      <div :class="$style.wheelStepsBlock">
        <div v-for="(step, i) in data.steps" :key="i" :class="$style.wheelStepsItem">
          <img
            :class="[$style.wheelStepsItemBorder, $style.wheelStepsItemBorderTop]"
            src="/images/wheel/img_border_neon1.png"
            alt=""
          />
          <img
            :class="[$style.wheelStepsItemBorder, $style.wheelStepsItemBorderBot]"
            src="/images/wheel/img_border_neon1.png"
            alt=""
          />
          <p :class="$style.wheelStepsItemNum">{{ i + 1 }}</p>
          <div :class="$style.wheelStepsItemContent">
            <Markdown :class="$style.wheelStepsItemTitle" :text="step.title" />
            <Markdown :class="$style.wheelStepsItemText" :text="step.text" />
          </div>
        </div>
      </div>
      <div :class="$style.wheelPrizes">
        <p :class="$style.wheelPrizesTitle">{{ data.prizesText }}</p>
        <ul :class="$style.wheelPrizesList">
          <li v-for="(prize, i) in wheelPrizesData" :key="i" :class="$style.wheelPrizesItem">
            <div :class="$style.wheelPrizesItemCombination">
              <img :src="prize.image" alt="" />
              <img :src="prize.image" alt="" />
              <img :src="prize.image" alt="" />
            </div>
            <div :class="$style.wheelPrizesItemContent">
              <p :class="$style.wheelPrizesItemTitle">{{ prize.title }}</p>
              <Markdown :class="$style.wheelPrizesItemPrize" :text="prize.value" />
            </div>
          </li>
        </ul>
      </div>
      <BitAccordionItem :class="$style.wheelRules" :title="rulesTitle">
        <Markdown :text="data.rulesText" />
      </BitAccordionItem>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { BitAccordionItem } from '@bit/ui-kit';
import Markdown from '~/components/markdown.vue';
import { useStoreModals } from '~/stores/abstract/modals';
import { useStoreRoot } from '~/stores/abstract/root';
import { useStoreSsLootboxes } from '~/stores/softswiss/lootboxes';
import { useStoreSsPlayer } from '~/stores/softswiss/player';
import { useStoreButtonTitles } from '~/stores/strapi/buttonTitles';
import { useStoreComponents } from '~/stores/strapi/components';
import { useStoreFortuneWheel } from '~/stores/strapi/fortuneWheel';
import { EModalName } from '~/types/modals';
import { strapiFileParser } from '~/utils/strapiComponents/addStrapiData';
import { findIfPassesByGroups } from '~/utils/strapiComponents/filters';
import type { ComponentInfoPageBlocksWheelBlock } from '~/types/infoPages';

const props = defineProps({
  data: {
    type: Object as PropType<ComponentInfoPageBlocksWheelBlock>,
    required: true,
  },
});
const components = useStoreComponents();
const storeModals = useStoreModals();
const storeSsLootboxes = useStoreSsLootboxes();
const storeSsPlayer = useStoreSsPlayer();
const storeButtons = useStoreButtonTitles();
const storeWheel = useStoreFortuneWheel();

const animationDuration = 1;
const boxesRefs = ref<HTMLElement[]>([]);
const doorsRefs = ref<HTMLElement[]>([]);
const pool = ref<string[]>([]);

const heightRatio = 0.6;
const offsetRatio = 0.8;

const wheelState = ref<'win' | 'spin' | null>('spin');

const prevWidth = ref(0);
const winNumber = ref(1);
const winPrizeTitle = ref<string>('');
const isLoading = ref(true);

const isAllowedByGroups = computed(() => {
  if (!storeSsPlayer.isPlayerAuthorised && props.data.groupFilter?.hideForGuests) {
    return false;
  }

  const isAllowedByGroups = findIfPassesByGroups(
    props.data.groupFilter?.includedGroups,
    props.data.groupFilter?.excludedGroups
  );

  if (!isAllowedByGroups) {
    return false;
  }

  return true;
});

const rulesTitle = computed(() => components.races.rulesTitle);
const numberOfSpins = computed(() => storeSsLootboxes.wheelLootboxes.length);

const wheelPrizesData = computed(() =>
  storeWheel.wheeltems.map((item) => ({
    image: item.image.url,
    title: item.prizeName,
    value: item.prizeValue,
  }))
);

const prizesImages = computed(() => storeWheel.wheeltems.map((item) => item.image.url));

const buttonTitle = computed(() => {
  if (!storeSsPlayer.hasAuthToken && !storeSsPlayer.isPlayerAuthorised) {
    return storeButtons.signup;
  }
  if (storeSsLootboxes.isLoadedOnce && numberOfSpins.value) {
    return props.data.btnSpin;
  }
  return props.data.btnDepostToSpin;
});

const itemsWheel = computed(() => {
  return getRandomItems(prizesImages.value, 9);
});
const itemsEndWheel = computed(() => {
  return getRandomItems(prizesImages.value, 3);
});
const winningItem = computed(() => {
  const indxPrize = storeWheel.wheeltems.findIndex((el) => el.prizeTitleBO === winPrizeTitle.value);
  return Array(3).fill(prizesImages.value[indxPrize]);
});

function getRandomItems<T>(arr: T[], count: number): T[] {
  const result: T[] = [];
  for (let i = 0; i < count; i++) {
    const randomIndex = Math.floor(Math.random() * arr.length);
    result.push(arr[randomIndex]);
  }
  return result;
}

function shuffle([...arr]) {
  let m = arr.length;
  while (m) {
    const i = Math.floor(Math.random() * m--);
    [arr[m], arr[i]] = [arr[i], arr[m]];
  }
  return arr;
}

const imgCache = new Map<string, HTMLImageElement>();
function getCachedImage(src: string): HTMLImageElement {
  if (!imgCache.has(src)) {
    const img = document.createElement('img');
    img.src = src;
    imgCache.set(src, img);
  }
  return imgCache.get(src)!.cloneNode(true) as HTMLImageElement;
}

function transitionStartHandler(e: Event) {
  const target = e.currentTarget as HTMLElement;
  wheelState.value = 'spin';
  target.dataset.ended = undefined;
  target.querySelectorAll<HTMLElement>('.box').forEach((box) => {
    box.style.filter = 'blur(3px)';
  });
}
function transitionEndHandler(e: Event, idDoor: number, testSpin = false) {
  const target = e.currentTarget as HTMLElement;
  target.dataset.ended = 'true';
  const allBoxes = target.querySelectorAll<HTMLElement>('.box');
  if (!testSpin) {
    setTimeout(() => {
      wheelState.value = 'win';
    }, 2000);
    setTimeout(() => {
      storeModals.openModal(EModalName.wheel_win, {
        prizeTitle: winPrizeTitle.value,
        doNotAttachToQuery: true,
      });
      wheelState.value = null;
    }, 4000);
  } else {
    setTimeout(() => {
      isLoading.value = false;
      wheelState.value = null;
    }, 1000);
  }

  allBoxes.forEach((box, index) => {
    box.style.filter = 'blur(0)';
    if (index === 1) {
      setTimeout(() => {
        box.classList.remove('wiggle-effect');
        if (!testSpin) {
          box.classList.add(`wow${idDoor + 1}`);
        }
      }, 1800);
      setTimeout(() => {
        box.classList.remove(`wow${idDoor + 1}`);
      }, 4000);
    }
    if (index < 3) {
      box.style.setProperty('--final-y', `${0}px`);
      box.classList.add('wiggle-effect');
      setTimeout(() => {
        box.classList.remove('wiggle-effect');
      }, 1800);
    }
  });
}

function init(firstInit = true, testSpin = false) {
  const arr: string[] = [];
  const doors = doorsRefs.value;
  const winningWheelItems = testSpin ? getRandomItems(prizesImages.value, 3) : winningItem.value;
  for (let n = 0; n < doors.length; n++) {
    arr.push(...shuffle(itemsWheel.value), ...winningWheelItems, ...itemsEndWheel.value);
  }
  pool.value = arr;

  doors.forEach((door, id) => {
    door.dataset.spinned = '0';

    const boxes = door.querySelector('.boxes');
    const boxesClone = boxes!.cloneNode(false) as HTMLElement;

    if (!firstInit) {
      boxesClone.addEventListener('transitionstart', transitionStartHandler, { once: true });
      boxesClone.addEventListener(
        'transitionend',
        (e: Event) => transitionEndHandler(e, id, testSpin),
        {
          once: true,
        }
      );
    }

    const boxWidth = door.clientWidth;
    const boxHeight = door.clientHeight * heightRatio;

    for (let i = id; i < pool.value.length; i += 3) {
      const box = document.createElement('div');
      box.classList.add('box');
      box.style.width = `${boxWidth}px`;
      box.style.height = `${boxHeight}px`;

      const img = getCachedImage(pool.value[i]);
      box.appendChild(img);
      boxesClone.insertBefore(box, boxesClone.firstChild);
    }

    const totalRows = Math.floor(pool.value.length / 3);
    const translateY = boxHeight * (totalRows - 1) - door.clientHeight * offsetRatio;

    boxesClone.style.transitionDuration = `${animationDuration > 0 ? animationDuration : 1}s`;
    boxesClone.style.transform = `translateY(-${translateY}px)`;

    door.replaceChild(boxesClone, boxes!);
    boxesRefs.value[id] = boxesClone;
  });
}

const spin = (options?: { testSpin: boolean }) => {
  init(false, options?.testSpin);
  setTimeout(async () => {
    const doorFirst = doorsRefs.value[0];
    for (const boxes of boxesRefs.value) {
      const duration = parseInt(boxes.style.transitionDuration);
      boxes.style.transform = `translateY(-${doorFirst.clientHeight * (1 - heightRatio)}px)`;
      await new Promise((resolve) => setTimeout(resolve, duration * 100));
    }
    if (!options?.testSpin) {
      isLoading.value = false;
    }
  }, 1000);
};

const updateBoxesSizes = () => {
  if (prevWidth.value !== window.innerWidth) {
    prevWidth.value = window.innerWidth;
    doorsRefs.value.forEach((door, i) => {
      const boxesHolder = boxesRefs.value[i];
      const heightBox = door.clientHeight * heightRatio;
      const boxes = boxesHolder.querySelectorAll<HTMLElement>('.box');
      boxes.forEach((box) => {
        box.style.width = door.clientWidth + 'px';
        box.style.height = heightBox + 'px';
      });

      boxesHolder.style.transitionDuration = `${animationDuration > 0 ? animationDuration : 1}s`;
      if (boxesHolder.dataset.ended) {
        boxesHolder.style.transform = `translateY(-${door.clientHeight * (1 - heightRatio)}px)`;
      } else {
        boxesHolder.style.transform = `translateY(-${
          heightBox * (pool.value.length / 3 - 1) - door.clientHeight * offsetRatio
        }px)`;
      }
    });
  }
};

const handleSpinClick = async () => {
  if (!storeSsPlayer.hasAuthToken && !storeSsPlayer.isPlayerAuthorised) {
    storeModals.openLoginOrSignupModal({ button_place: 'wheel_of_fortune' });
  } else if (numberOfSpins.value) {
    isLoading.value = true;
    const firstActiveLootbox = storeSsLootboxes.wheelLootboxes[0];

    const sortedBonusesByWeight = [...firstActiveLootbox.items].sort((a, b) => b.weight - a.weight);
    const prize = await storeSsLootboxes.activateLootbox(firstActiveLootbox!.id);
    if (!prize) {
      return;
    }
    const winIndex = sortedBonusesByWeight.findIndex((item) => item.id === prize.id);
    winNumber.value = winIndex;
    winPrizeTitle.value = prize.bonuses[0].title;
    setTimeout(() => {
      spin();
    }, 100);
  } else {
    storeModals.openModal(EModalName.deposit, {
      bonusId: 'wheel-of-fortune',
      bonuscode: 'WHEEL',
      gtmParams: { button_place: 'wheel_of_fortune' },
    });
  }
};

watch(
  () => isAllowedByGroups.value,
  (val, oldVal) => {
    if (!val && val !== oldVal) {
      return navigateTo({
        path: useStoreRoot().localePath('/'),
      });
    }
  },
  { immediate: true }
);

onMounted(() => {
  init();
  spin({ testSpin: true });
  window.addEventListener('resize', updateBoxesSizes);
  storeSsLootboxes.loadLootboxes();
});

useHead({
  link: [
    {
      rel: 'preload',
      as: 'image',
      href: strapiFileParser(props.data.backgroundImage).url,
    },
    {
      rel: 'preload',
      as: 'image',
      href: strapiFileParser(props.data.wheelImageDefault).url,
    },
    {
      rel: 'preload',
      as: 'image',
      href: strapiFileParser(props.data.wheelImageWin).url,
    },
  ],
});
</script>

<style lang="scss" module src="./fortuneWheelBlock.scss"></style>
